import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatIDR, parseIDR, formatDate, todayISO } from "@/lib/format";
import { Card, PageHeader, Button, Field, Input, Modal, Badge, Select, KPI } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import MultiCheckFilter from "@/components/MultiCheckFilter";
import { useTableFilter } from "@/hooks/useTableFilter";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/contexts/AuthContext";
import { Trash2, Wallet, Pencil, Printer, Plus, Upload, FileText, Eye, ListChecks, Mail } from "lucide-react";
import PdfViewerModal from "@/components/pdf/PdfViewerModal";
import { DatePicker } from "@/components/DatePicker";

const blankPay = {
  paid_amount: 0, payment_date: todayISO(), method: "transfer", note: "",
  proofs: [],
};

export default function BillingList() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);    // invoice being viewed/paid
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState(blankPay);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  // Iter 64 (#8) — Popout PDF viewer instead of opening raw URL.
  const [pdfViewerInv, setPdfViewerInv] = useState(null);

  // Summary card filter (independent of table filter)
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const todayISOStr = today.toISOString().slice(0, 10);
  const [sumStart, setSumStart] = useState(monthStart);
  const [sumEnd, setSumEnd] = useState(todayISOStr);
  const [summary, setSummary] = useState({ total_ppn: 0, total_billed: 0, receivables: 0 });

  const { filtered: textFiltered, FilterBar } = useTableFilter(rows, ["invoice_number", "client_name", "po_number", "status", "category", "items.material_name", "recap_title"], "Cari invoice / klien / material…");
  // Iter v34m (#3) — Unified multi-check filter (status + kategori).
  const [mfilter, setMfilter] = useState({ status: [], category: [] });
  const [categories, setCategories] = useState([]);
  const _byStatus = mfilter.status.length > 0
    ? textFiltered.filter((r) => mfilter.status.includes(r.status || ""))
    : textFiltered;
  const filtered = mfilter.category.length > 0
    ? _byStatus.filter((r) => mfilter.category.includes(r.category || ""))
    : _byStatus;
  // Iter v34{aa} — Filter "Reminder Terakhir": semua / belum pernah / sudah pernah.
  const [reminderFilter, setReminderFilter] = useState("all");
  // Iter v34cc (#1) — Apply date range filter ke tabel/pagination juga (bukan hanya KPI).
  // Sebelumnya list menampilkan semua invoice tanpa filter tanggal walau KPI sudah difilter.
  const filteredWithReminder = React.useMemo(() => {
    let out = filtered;
    if (reminderFilter === "never") out = out.filter((r) => !r.last_reminder_email_at);
    else if (reminderFilter === "sent") out = out.filter((r) => !!r.last_reminder_email_at);
    // Date range guard (sumStart..sumEnd inclusive).
    if (sumStart || sumEnd) {
      out = out.filter((r) => {
        const d = (r.invoice_date || (r.created_at || "").slice(0, 10));
        return (!sumStart || d >= sumStart) && (!sumEnd || d <= sumEnd);
      });
    }
    return out;
  }, [filtered, reminderFilter, sumStart, sumEnd]);
  const { pageItems, Pagination } = usePagination(filteredWithReminder, { perPage: 14 });

  // Iter v34m (#2) — Filtered summary derived from currently-filtered rows
  // (respects date range + status + category + text search).
  // Iter v34bu (#5) — FIX: receivables sekarang juga mengecek inRange (sebelumnya
  // piutang selalu masuk semua tanggal → total tidak sesuai filter). Cancelled
  // invoice di-skip dari semua totalan (PPN, billed, piutang).
  const filteredSummary = React.useMemo(() => {
    const inRange = (r) => {
      const d = (r.invoice_date || (r.created_at || "").slice(0, 10));
      return (!sumStart || d >= sumStart) && (!sumEnd || d <= sumEnd);
    };
    let ppn = 0, billed = 0, receivables = 0;
    for (const r of filtered) {
      if ((r.status || "") === "cancelled") continue;
      if (!inRange(r)) continue;
      const grand = parseFloat(r.grand_total || 0);
      const paid = parseFloat(r.paid_amount || 0);
      const outstanding = Math.max(0, grand - paid);
      ppn += parseFloat(r.vat || 0);
      billed += grand;
      if (outstanding > 0) receivables += outstanding;
    }
    return {
      total_ppn: Math.round(ppn * 100) / 100,
      total_billed: Math.round(billed * 100) / 100,
      receivables: Math.round(receivables * 100) / 100,
    };
  }, [filtered, sumStart, sumEnd]);

  const loadSummary = () => {
    api.get(`/invoices-summary?start=${sumStart}&end=${sumEnd}`).then((r) => setSummary(r.data));
  };

  const load = () => api.get("/invoices").then((r) => setRows(r.data));
  useEffect(() => { load(); loadSummary(); api.get("/masters/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSummary(); }, [sumStart, sumEnd]);

  const openNewPayment = (inv) => {
    setActive(inv);
    const outstanding = (inv.grand_total || 0) - (inv.paid_amount || 0);
    setPay({ ...blankPay, paid_amount: outstanding });
    setEditingPaymentId(null);
    setPayOpen(true);
  };

  const openEditPayment = (p) => {
    setEditingPaymentId(p.id);
    // Iter v34al — Multi-proof: gunakan array `proofs` bila ada, fallback ke
    // legacy single-proof field.
    const proofs = Array.isArray(p.proofs) && p.proofs.length > 0
      ? p.proofs.map((x) => ({ id: x.id, path: x.path, filename: x.filename, content_type: x.content_type }))
      : (p.proof_id ? [{ id: p.proof_id, path: p.proof_path, filename: p.proof_filename, content_type: p.proof_content_type }] : []);
    setPay({
      paid_amount: p.amount, payment_date: p.date, method: p.method || "transfer",
      note: p.note || "",
      proofs,
    });
    setPayOpen(true);
  };

  const uploadProof = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file); fd.append("kind", "payment");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: r.data.id, path: r.data.path, filename: r.data.filename, content_type: r.data.content_type });
      }
      setPay((p) => ({ ...p, proofs: [...(p.proofs || []), ...uploaded] }));
      toast.success(files.length > 1 ? `${files.length} bukti terunggah` : "Bukti terunggah");
      // reset input agar file yang sama bisa dipilih lagi
      e.target.value = "";
    } catch (err) { toast.error("Gagal upload"); }
    finally { setUploading(false); }
  };

  const removeProof = (idx) => {
    setPay((p) => ({ ...p, proofs: (p.proofs || []).filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    if (!active) return;
    try {
      const proofs = pay.proofs || [];
      // Kirim juga legacy single-proof (yang pertama) supaya kompatibel dengan backend lama.
      const first = proofs[0] || {};
      const payload = {
        paid_amount: parseIDR(pay.paid_amount),
        payment_date: pay.payment_date,
        method: pay.method,
        note: pay.note,
        proofs,
        proof_id: first.id || "",
        proof_path: first.path || "",
        proof_filename: first.filename || "",
        proof_content_type: first.content_type || "",
      };
      if (editingPaymentId) {
        await api.patch(`/invoices/${active.id}/payment/${editingPaymentId}`, payload);
        toast.success("Pembayaran diperbarui");
      } else {
        await api.post(`/invoices/${active.id}/payment`, payload);
        toast.success("Pembayaran tercatat");
      }
      setPayOpen(false);
      setEditingPaymentId(null);
      const updated = await api.get(`/invoices/${active.id}`);
      setActive(updated.data);
      load();
    } catch (e) { toast.error("Gagal: " + (e?.response?.data?.detail || "")); }
  };

  const delPayment = async (paymentId) => {
    if (!window.confirm("Hapus pembayaran ini?")) return;
    try {
      await api.delete(`/invoices/${active.id}/payment/${paymentId}`);
      const updated = await api.get(`/invoices/${active.id}`);
      setActive(updated.data);
      load();
      toast.success("Pembayaran dihapus");
    } catch (e) { toast.error("Gagal"); }
  };

  const del = async (id) => { if (!window.confirm("Hapus invoice?")) return; await api.delete(`/invoices/${id}`); load(); };

  // Iter v34z — kirim email pengingat tagihan jatuh tempo (manual).
  const sendReminder = async (inv, overdueDays) => {
    if (!window.confirm(`Kirim email pengingat ke klien "${inv.client_name}" untuk tagihan ${inv.invoice_number} (H+${overdueDays})?`)) return;
    try {
      const res = await api.post(`/invoices/${inv.id}/send-overdue-reminder`);
      toast.success(`Email pengingat terkirim ke ${res.data.to}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim email pengingat");
    }
  };

  // Iter v34{aa} — Batch: kirim email pengingat ke SEMUA invoice overdue.
  const [bulkSending, setBulkSending] = useState(false);
  const sendBulkReminders = async () => {
    if (!window.confirm("Kirim email pengingat ke SEMUA tagihan yang sudah lewat jatuh tempo & belum lunas?")) return;
    setBulkSending(true);
    try {
      const res = await api.post(`/invoices/send-overdue-reminders-bulk`);
      const d = res.data || {};
      const msg = `Total ${d.total} overdue · ${d.sent} terkirim · ${d.failed} gagal`;
      if ((d.failed || 0) > 0) toast.warning(msg);
      else toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim batch reminder");
    } finally {
      setBulkSending(false);
    }
  };

  // Iter v34{aa}+v34{ab} — Compute reminder availability for the PDF viewer's
  // mail action. Active when due_date ≤ threshold days OR already overdue.
  const [settings, setSettings] = useState({ reminder_days_threshold: 7 });
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || { reminder_days_threshold: 7 })).catch(() => {});
  }, []);
  const reminderThresholdDays = settings?.reminder_days_threshold ?? 7;
  const reminderActiveFor = (inv) => {
    if (!inv?.due_date || inv.status === "paid" || (inv.grand_total - (inv.paid_amount || 0)) <= 0) return { active: false, overdueDays: 0 };
    const due = new Date(inv.due_date);
    if (isNaN(due.getTime())) return { active: false, overdueDays: 0 };
    const t0 = new Date(todayISOStr);
    const diff = Math.floor((t0 - due) / (1000 * 60 * 60 * 24));
    if (diff > 0) return { active: true, overdueDays: diff, label: `Pengingat H+${diff}` };
    if (diff >= -reminderThresholdDays) return { active: true, overdueDays: diff, label: diff === 0 ? "Pengingat (Hari ini)" : `Pengingat (H${diff})` };
    return { active: false, overdueDays: 0 };
  };

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader overline="Keuangan · Penagihan" title={t("billing_list")}
        description="Daftar invoice & pembayaran. Bukti pembayaran dapat diunggah dan dilihat ulang."
      />

      {/* Iter v34m (#2) — Summary cards reflect ALL active filters (status, category, date, search). */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI testid="sum-ppn" label={`Jumlah PPN · ${sumStart} → ${sumEnd}`} value={formatIDR(filteredSummary.total_ppn)} sub="Mengikuti filter aktif" accent="info" />
        <KPI testid="sum-billed" label={`Total Tagihan · ${sumStart} → ${sumEnd}`} value={formatIDR(filteredSummary.total_billed)} sub="Mengikuti filter aktif" accent="terra" />
        <KPI testid="sum-receivables" label="Piutang · Filter aktif" value={formatIDR(filteredSummary.receivables)} sub="Outstanding semua tagihan di hasil filter" accent="moss" />
      </div>

      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex items-end gap-2">
          <Field label="Dari"><DatePicker value={sumStart} onChange={(e) => setSumStart(e.target.value)} data-testid="sum-start" /></Field>
          <Field label="Sampai"><DatePicker value={sumEnd} onChange={(e) => setSumEnd(e.target.value)} data-testid="sum-end" /></Field>
          <Button variant="ghost" onClick={() => { setSumStart(monthStart); setSumEnd(todayISOStr); }} className="!h-10 text-xs">Reset Bulan</Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {FilterBar}
          <MultiCheckFilter
            testidPrefix="bl-filter"
            value={mfilter}
            onChange={setMfilter}
            groups={[
              {
                key: "status",
                label: "Status Pembayaran",
                options: [
                  { value: "paid", label: "Lunas" },
                  { value: "partial", label: "Partial" },
                  { value: "unpaid", label: "Belum Dibayar" },
                ],
              },
              {
                key: "category",
                label: "Kategori",
                options: categories.map((c) => ({ value: c.name, label: c.name })),
              },
            ]}
          />
          <Select value={reminderFilter} onChange={(e) => setReminderFilter(e.target.value)} className="!h-10 !text-xs" data-testid="reminder-filter">
            <option value="all">Reminder: Semua</option>
            <option value="never">Belum Pernah</option>
            <option value="sent">Sudah Pernah</option>
          </Select>
          {hasPerm("invoice.edit") && (
            <Button variant="ghost" onClick={sendBulkReminders} disabled={bulkSending} className="!h-10 !text-xs gap-1" data-testid="send-bulk-reminder-btn">
              <Mail className="h-3.5 w-3.5" /> {bulkSending ? "Mengirim…" : "Kirim Reminder Overdue"}
            </Button>
          )}
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">No Invoice</th>
              <th className="py-3 px-3">Tanggal</th>
              <th className="py-3 px-3">Klien</th>
              <th className="py-3 px-3 text-right">{t("grand_total")}</th>
              <th className="py-3 px-3 text-right">{t("paid_amount")}</th>
              <th className="py-3 px-3 text-right">Sisa</th>
              <th className="py-3 px-3">Status</th>
              <th className="py-3 px-3">Reminder Terakhir</th>
              <th className="py-3 px-3 text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="py-12 text-center text-stone-400">Tidak ada invoice.</td></tr>}
            {pageItems.map((r, idx) => {
              const outstanding = (r.grand_total || 0) - (r.paid_amount || 0);
              const tone = r.status === "paid" ? "success" : r.status === "partial" ? "warn" : "danger";
              // Iter v34z — Tombol email pengingat hanya tampil bila jatuh tempo terlampaui & saldo > 0.
              let overdueDays = 0;
              if (r.due_date && r.status !== "paid" && outstanding > 0) {
                const d = new Date(r.due_date);
                const t0 = new Date(todayISOStr);
                if (!isNaN(d.getTime())) {
                  overdueDays = Math.floor((t0 - d) / (1000 * 60 * 60 * 24));
                }
              }
              const isOverdue = overdueDays > 0;
              return (
                <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`inv-row-${idx}`}>
                  <td className="py-2.5 px-3 font-mono">{r.invoice_number}</td>
                  <td className="py-2.5 px-3 font-mono text-stone-600">{formatDate(r.created_at)}</td>
                  <td className="py-2.5 px-3 font-medium">{r.client_name}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num">{formatIDR(r.grand_total)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-emerald-700">{formatIDR(r.paid_amount)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-amber-700">{formatIDR(outstanding)}</td>
                  <td className="py-2.5 px-3">
                    <Badge tone={tone}>{r.status}</Badge>
                    {isOverdue && <span className="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-red-100 text-red-700 rounded uppercase" data-testid={`overdue-badge-${idx}`}>H+{overdueDays}</span>}
                  </td>
                  <td className="py-2.5 px-3 text-xs text-stone-600 whitespace-nowrap" data-testid={`reminder-cell-${idx}`}>
                    {r.last_reminder_email_at ? (
                      <span className="font-mono">{formatDate(r.last_reminder_email_at)}{(r.reminder_email_count || 0) > 1 ? ` · ${r.reminder_email_count}×` : ""}</span>
                    ) : (
                      <span className="text-stone-400 italic">Belum pernah</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    <button onClick={() => { setActive(r); setPaymentsOpen(true); }} className="text-stone-600 hover:text-terra-600 px-2 inline-flex items-center" title="Riwayat Pembayaran" data-testid={`payments-list-${idx}`}>
                      <ListChecks className="h-3.5 w-3.5" />
                    </button>
                    {hasPerm("invoice.edit") && <button onClick={() => openNewPayment(r)} className="text-stone-600 hover:text-terra-600 px-2 inline-flex items-center" title="Catat Pembayaran" data-testid={`pay-btn-${idx}`}>
                      <Wallet className="h-3.5 w-3.5" />
                    </button>}
                    {hasPerm("invoice.edit") && <button onClick={() => navigate(`/billing?edit=${r.id}`)} className="text-stone-600 hover:text-terra-600 px-2" title="Edit" data-testid={`edit-inv-${idx}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>}
                    {hasPerm("invoice.print") && <button onClick={() => setPdfViewerInv(r)} className="text-stone-600 hover:text-terra-600 px-2 inline-flex items-center" title="Lihat PDF" data-testid={`view-inv-pdf-${idx}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </button>}
                    {hasPerm("invoice.delete") && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2" data-testid={`delete-inv-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      {Pagination}

      {/* Payment record/edit modal */}
      <Modal open={payOpen} onClose={() => setPayOpen(false)} title={editingPaymentId ? "Edit Pembayaran" : t("record_payment")}>
        {active && (
          <div className="space-y-4">
            <div className="bg-stone-50 border border-stone-200 p-3 text-sm">
              <p className="font-mono text-xs text-stone-500">{active.invoice_number}</p>
              <p className="font-medium">{active.client_name}</p>
              <p className="font-mono-num">Sisa: {formatIDR((active.grand_total || 0) - (active.paid_amount || 0))}</p>
            </div>
            <Field label={t("paid_amount")}><Input className="font-mono-num" value={pay.paid_amount} onChange={(e) => setPay({ ...pay, paid_amount: e.target.value })} data-testid="pay-amount" /></Field>
            <Field label={t("payment_date")}><DatePicker value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} data-testid="pay-date" /></Field>
            <Field label={t("method")}>
              <Select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                <option value="transfer">Transfer</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
              </Select>
            </Field>
            <Field label={t("notes")}><Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></Field>

            <Field label="Bukti Pembayaran (JPG/PNG/PDF · Bisa upload lebih dari satu)">
              <div className="space-y-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm w-fit">
                  <Upload className="h-4 w-4" />{uploading ? "Mengunggah…" : (pay.proofs?.length ? "Tambah bukti lagi" : "Pilih file (multi)")}
                  <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={uploadProof} className="hidden" data-testid="pay-proof-input" />
                </label>
                {(pay.proofs || []).length > 0 && (
                  <div className="space-y-1.5 border border-stone-200 rounded-sm p-2 bg-stone-50" data-testid="pay-proof-list">
                    {(pay.proofs || []).map((pf, idx) => (
                      <div key={pf.id || idx} className="flex items-center gap-2 text-xs" data-testid={`pay-proof-item-${idx}`}>
                        <FileText className="h-3.5 w-3.5 text-stone-500 shrink-0" />
                        <span className="text-emerald-800 truncate flex-1">{pf.filename || "(tanpa nama)"}</span>
                        <button type="button" onClick={() => setPreviewId({ id: pf.id, name: pf.filename, ct: pf.content_type })}
                          className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 shrink-0" data-testid={`pay-proof-preview-${idx}`}>
                          <Eye className="h-3.5 w-3.5" />Preview
                        </button>
                        <button type="button" onClick={() => removeProof(idx)}
                          className="text-stone-500 hover:text-red-700 shrink-0" data-testid={`pay-proof-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Field>

            <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
              <Button variant="ghost" onClick={() => setPayOpen(false)}>{t("cancel")}</Button>
              <Button onClick={submit} data-testid="save-payment-btn">{t("save")}</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Payments history modal */}
      <Modal open={paymentsOpen} onClose={() => setPaymentsOpen(false)} title="Riwayat Pembayaran" size="lg">
        {active && (
          <div className="space-y-4">
            <div className="bg-stone-50 border border-stone-200 p-3 text-sm flex items-center justify-between">
              <div>
                <p className="font-mono text-xs text-stone-500">{active.invoice_number}</p>
                <p className="font-medium">{active.client_name}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-stone-500">Grand Total</p>
                <p className="font-mono-num text-lg">{formatIDR(active.grand_total)}</p>
                <p className="text-xs text-emerald-700">Dibayar: <span className="font-mono-num">{formatIDR(active.paid_amount)}</span></p>
              </div>
            </div>

            <table className="w-full text-sm border border-stone-200">
              <thead><tr className="bg-stone-50 text-left text-[10px] uppercase tracking-widest font-semibold text-stone-600">
                <th className="py-2 px-3">Tanggal</th><th className="py-2 px-3 text-right">Jumlah</th>
                <th className="py-2 px-3">Metode</th><th className="py-2 px-3">Catatan</th>
                <th className="py-2 px-3">Bukti</th><th className="py-2 px-3 text-right">Aksi</th>
              </tr></thead>
              <tbody>
                {(active.payments || []).length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-stone-400 text-xs">Belum ada pembayaran.</td></tr>
                )}
                {(active.payments || []).map((p, i) => (
                  <tr key={p.id || i} className="border-t border-stone-100" data-testid={`payment-row-${i}`}>
                    <td className="py-2 px-3 font-mono text-xs">{formatDate(p.date)}</td>
                    <td className="py-2 px-3 text-right font-mono-num">{formatIDR(p.amount)}</td>
                    <td className="py-2 px-3 capitalize">{p.method || "—"}</td>
                    <td className="py-2 px-3 text-xs text-stone-600">{p.note || "—"}</td>
                    <td className="py-2 px-3">{(() => {
                      const proofs = (Array.isArray(p.proofs) && p.proofs.length > 0)
                        ? p.proofs
                        : (p.proof_id ? [{ id: p.proof_id, filename: p.proof_filename, content_type: p.proof_content_type }] : []);
                      if (proofs.length === 0) return <span className="text-stone-300">—</span>;
                      return (
                        <div className="flex flex-wrap items-center gap-1.5" data-testid={`proof-list-${i}`}>
                          {proofs.map((pf, pi) => (
                            <button key={pf.id || pi} onClick={() => setPreviewId({ id: pf.id, name: pf.filename, ct: pf.content_type })}
                              className="inline-flex items-center gap-1 text-terra-600 hover:text-terra-700 hover:bg-terra-50 border border-stone-200 px-1.5 py-0.5 text-[11px]"
                              title={pf.filename}
                              data-testid={`proof-preview-${i}-${pi}`}>
                              <Eye className="h-3 w-3" /><span>{pi + 1}</span>
                            </button>
                          ))}
                          {proofs.length > 1 && (
                            <span className="text-[10px] text-stone-500" data-testid={`proof-count-${i}`}>{proofs.length} file</span>
                          )}
                        </div>
                      );
                    })()}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {p.id && <button onClick={() => openEditPayment(p)} className="text-stone-500 hover:text-terra-600 px-2" data-testid={`edit-payment-${i}`}><Pencil className="h-3.5 w-3.5" /></button>}
                      {p.id && <button onClick={() => delPayment(p.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-between pt-4 border-t border-stone-200">
              <Button variant="ghost" onClick={() => setPaymentsOpen(false)}>Tutup</Button>
              <Button onClick={() => { setPaymentsOpen(false); openNewPayment(active); }} data-testid="new-payment-btn"><Plus className="h-4 w-4" />Tambah Pembayaran</Button>
            </div>
          </div>
        )}
      </Modal>

      <FilePreview open={!!previewId} onClose={() => setPreviewId(null)} fileId={previewId?.id} filename={previewId?.name} contentType={previewId?.ct} />

      {/* Iter 64 (#8) — Popout PDF viewer for invoice. */}
      <PdfViewerModal
        open={!!pdfViewerInv}
        onClose={() => setPdfViewerInv(null)}
        pdfUrl={pdfViewerInv ? `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${pdfViewerInv.id}/pdf` : ""}
        fileName={`Invoice-${pdfViewerInv?.invoice_number || pdfViewerInv?.id || "doc"}.pdf`}
        title={`Invoice ${pdfViewerInv?.invoice_number || ""}`}
        reminderAction={
          pdfViewerInv && hasPerm("invoice.edit") && reminderActiveFor(pdfViewerInv).active ? {
            label: reminderActiveFor(pdfViewerInv).label,
            enabled: true,
            testid: "pdf-viewer-reminder-btn",
            onClick: async () => {
              const info = reminderActiveFor(pdfViewerInv);
              if (!info.active) return;
              if (!window.confirm(`Kirim email pengingat ke klien "${pdfViewerInv.client_name}" untuk tagihan ${pdfViewerInv.invoice_number} (${info.label})?`)) return;
              try {
                const res = await api.post(`/invoices/${pdfViewerInv.id}/send-overdue-reminder`);
                toast.success(`Email pengingat terkirim ke ${res.data.to}`);
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Gagal mengirim email pengingat");
              }
            },
          } : null
        }
      />
    </div>
    </div>
  );
}
